/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react/no-unstable-nested-components */
import GridContainerWrapper from 'components/@common/GridContainerWrapper'
import {
  useAddons,
  useAffiliate,
  useDataLayer,
  useLegalDeclaration,
  usePrice,
  useRiskData,
  useScheme,
  useVehicle,
  useQuote,
} from 'hooks'
import { InitialStateType } from 'initialState'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Grid } from 'unsemantic'
import useOptimizelyExperiment from 'hooks/experiments/useOptimizelyExperiment'
import { ReactSDKClient, withOptimizely } from '@optimizely/react-sdk'
import { UserInfo } from '@optimizely/react-sdk/dist/utils'
import underwriterApi from '../../api/underwriterApi'
import { WhiteLabelledLink } from '../../components/common/A'
import { H2 } from '../../components/common/H2'
import { H3 } from '../../components/common/H3'
import { DeclarationModal } from '../../components/DeclarationModal'
import config from '../../config'
import { QUOTE_URL } from '../../services/constants'
import { updateStep } from '../App/actions'
import { requestLegalDeclarationSuccess } from '../LegalDeclarationModal/actions'
import {
  LegalDeclarationHeader,
  LegalDeclarationParagraph,
  LegalDeclarationWrapper,
} from './LegalDeclarationPage.styles'
import { LegalDeclarationPageWrapper } from './LegalDeclarationPageWrapper'
import { ShortDeclaration } from './ShortDeclaration'
import {
  LegalDeclarationAgreeAndContinueText,
  PolicyDocumentList,
} from './styles'
import Button from 'components/@common/Button'
import { trackAmplitudeEvent } from 'utils/analytics-helpers'
import { PolicyDocumentLogo } from 'containers/PolicyConfirmationPage/Sections/PolicyDocumentLogo'
import {
  PolicyDocumentLink,
  PolicyDocumentListItem,
  PolicyDocumentName,
} from 'containers/PolicyConfirmationPage/Sections/styles'
import { Card } from 'components/@common/Card'

interface LegalDeclarationPageProps {
  optimizely: ReactSDKClient | null
}

const LegalDeclarationPage: React.FC<LegalDeclarationPageProps> = ({
  optimizely,
}) => {
  const shouldUseSplitAddonsPage = useOptimizelyExperiment(
    optimizely,
    'addons_page',
    'page_enabled',
  )

  const [variant] = useState<string>('0')
  const [showLegalDec, setLegalDecOpen] = useState<boolean>(false)
  const [showOccupationDec, setOccupationDecOpen] = useState<boolean>(false)

  const [wordingLoaded, setWordingLoaded] = useState(false)
  const { availableAddons } = useAddons()

  const dispatch = useDispatch()
  const { legalDeclaration } = useLegalDeclaration()
  const { price } = usePrice()
  const { riskData } = useRiskData()
  const { quote } = useQuote()
  const { vehicleType } = useVehicle()
  const { scheme, isFirstLearner, isImpounded } = useScheme()
  const { affiliate } = useAffiliate()
  const { excess, breakdown, legalexpenses, gapinsurance } = useAddons()
  const history = useHistory()

  const legalDeclarationButtonDisabled = useSelector(
    (state: InitialStateType) => state.formButtons.legalDeclaration.disabled,
  )

  const queryString: string = useSelector(
    (state: InitialStateType) => state.queryString,
  )

  const myLicenceStatus = useSelector(
    (state: InitialStateType) => state.myLicence,
  )

  const { pushQuoteReferenceNumber } = useDataLayer()

  const handleContinueClick = () => {
    trackAmplitudeEvent('declaration_accepted', {
      quote_id: riskData.QuoteId,
      product_type: vehicleType,
      scheme: scheme,
      reference_number: quote.ReferenceNumber,
    })
    trackAmplitudeEvent('declarations_accepted', {
      quote_id: riskData.QuoteId,
      product_type: vehicleType,
      scheme: scheme,
      reference_number: quote.ReferenceNumber,
    })

    const existingUserId = localStorage.getItem('userId')
    const updatedUser: UserInfo = {
      id: existingUserId,
      attributes: {
        legalDeclarationPageClick: true,
        isTempcoverDirectCustomer: !queryString.includes('ref='),
      },
    }
    optimizely?.onReady().then(() => {
      optimizely?.setUser(updatedUser)
    })

    const url =
      shouldUseSplitAddonsPage && availableAddons && availableAddons.length
        ? `${QUOTE_URL}/addons${queryString}`
        : `${QUOTE_URL}/payment${queryString}`

    history.push(url)
  }

  const className = useMemo(() => {
    if (myLicenceStatus.DisabledBySetting) {
      return `uw_${price.Underwriter}`
    }
    if (myLicenceStatus.Enabled) {
      return `ab_mylicence_on uw_${price.Underwriter}`
    }
    return `ab_mylicence_off uw_${price.Underwriter}`
  }, [myLicenceStatus, price])

  useEffect(() => {
    trackAmplitudeEvent('declaration_pageview', {
      quote_id: riskData.QuoteId,
      product_type: vehicleType,
      scheme: scheme,
    })
    trackAmplitudeEvent('declarations_displayed', {
      quote_id: riskData.QuoteId,
      product_type: vehicleType,
      scheme: scheme,
    })

    dispatch(updateStep(2))
    pushQuoteReferenceNumber()
    underwriterApi
      .getLegalDeclaration(
        `/api/affiliates/${affiliate.Ref || 'tempcover'}/quotes/${
          riskData.QuoteId
        }/schemes/${scheme}/underwriters/${
          price.UnderwriterId
        }/allDeclarations`,
      )
      .then((result) => {
        dispatch(requestLegalDeclarationSuccess(result.data))
        setWordingLoaded(true)
      })
    if (document.getElementById('DisallowedOccupationListItem')) {
      const disallowedOccupationsList = document.getElementById(
        'DisallowedOccupationListItem',
      )
      const disallowedOccupationsText = document.createTextNode(
        'Not be in one of the following occupations: ',
      )
      const disallowedOccupationsLink = document.createElement('p')
      disallowedOccupationsLink.innerHTML = 'View'
      disallowedOccupationsLink.setAttribute('id', 'DisallowedOccupationsLink')
      disallowedOccupationsLink.onclick = () => {
        setOccupationDecOpen(true)
      }
      if (disallowedOccupationsList) {
        disallowedOccupationsList.appendChild(disallowedOccupationsText)
        disallowedOccupationsList.appendChild(disallowedOccupationsLink)
      }
    }
  }, [])

  const ShortDeclarationAB = () => {
    if (variant === '1') {
      return (
        <LegalDeclarationWrapper>
          <ShortDeclaration html={legalDeclaration.ShortText} />
        </LegalDeclarationWrapper>
      )
    }
    return <ShortDeclaration html={legalDeclaration.ShortText} />
  }

  const HeaderComponent: React.FC = ({ children }) => {
    if (variant === '1') {
      return (
        <LegalDeclarationHeader id='legaldeclaration-header'>
          {children}
        </LegalDeclarationHeader>
      )
    }
    return <H3 id='legaldeclaration-header'>{children}</H3>
  }

  const ParagraphComponent: React.FC = ({ children }) => {
    if (variant === '1') {
      return <LegalDeclarationParagraph>{children}</LegalDeclarationParagraph>
    }
    return (
      <LegalDeclarationAgreeAndContinueText>
        {children}
      </LegalDeclarationAgreeAndContinueText>
    )
  }

  const Wrapper: React.FC = ({ children }) => {
    if (variant === '1') {
      return <>{children}</>
    }
    return <LegalDeclarationPageWrapper>{children}</LegalDeclarationPageWrapper>
  }

  const CoverTypeText = useMemo(() => {
    if (riskData.Impounded) {
      return (
        <>
          <HeaderComponent>
            {price.CoverType || 'Fully comprehensive'} cover
          </HeaderComponent>
          <HeaderComponent>
            The policyholder <strong>MUST</strong> be the owner and registered
            keeper of the vehicle being insured.{' '}
          </HeaderComponent>
        </>
      )
    }
    if (isFirstLearner) {
      return (
        <HeaderComponent>
          <strong>{price.CoverType}</strong> cover with &pound;
          {price.CompulsoryExcess.toFixed(2)} Accidental Damage, Malicious
          Damage and Fire excess
          <br />
          £1000.00 Theft excess
        </HeaderComponent>
      )
    }

    const addons: String[] = []

    if (breakdown.selected) addons.push('RAC UK Breakdown Cover')

    if (legalexpenses.selected) addons.push('Legal Expenses Cover')
    if (gapinsurance.selected) addons.push('Free 30 day GAP Insurance')

    return (
      <HeaderComponent>
        {`${price.CoverType || 'Fully Comprehensive'} `}
        {addons.length ? `with ${addons.join(', ')} and ` : 'cover with '}
        {`£${price.CompulsoryExcess.toFixed(2)} excess`}
        {excess.selected && (
          <>
            <br />
            Excess Reduction Cover £{excess.coverLevel.toFixed(2)}
          </>
        )}
      </HeaderComponent>
    )
  }, [
    riskData.Impounded,
    isFirstLearner,
    breakdown.selected,
    legalexpenses.selected,
    gapinsurance.selected,
    HeaderComponent,
    price.CoverType,
    price.CompulsoryExcess,
    excess.selected,
    excess.coverLevel,
  ])

  return (
    <GridContainerWrapper>
      <Wrapper>
        <H2>Your policy is</H2>
        {CoverTypeText}

        <H2>Just to check...</H2>
        <Grid desktop='100'>
          <ShortDeclarationAB />
        </Grid>
        <Grid desktop='100'>
          <Card>
            <PolicyDocumentList>
              <PolicyDocumentListItem>
                <PolicyDocumentLink
                  style={{ textDecoration: 'underline' }}
                  id='FullDeclaration'
                  onClick={() => setLegalDecOpen(true)}
                >
                  <PolicyDocumentLogo name='FullDeclaration' />
                  <PolicyDocumentName>
                    Read full declaration here
                  </PolicyDocumentName>
                </PolicyDocumentLink>
              </PolicyDocumentListItem>
              <PolicyDocumentListItem>
                <PolicyDocumentLink
                  id='TempcoverContract'
                  href={`${config.BASE_API_URL}/api/motorQuotePolicies/${riskData.QuoteId}/documents/57`}
                  target='_blank'
                >
                  <PolicyDocumentLogo name='TempcoverContract' />
                  <PolicyDocumentName>
                    Download your Tempcover Contract
                  </PolicyDocumentName>
                </PolicyDocumentLink>
              </PolicyDocumentListItem>
              <PolicyDocumentListItem>
                <PolicyDocumentLink
                  id='PolicyWording'
                  href={`${config.BASE_API_URL}/api/motorQuotePolicies/${riskData.QuoteId}/documents/2`}
                  target='_blank'
                >
                  <PolicyDocumentLogo name='PolicyWording' />
                  <PolicyDocumentName>
                    Download policy wording here
                  </PolicyDocumentName>
                </PolicyDocumentLink>
              </PolicyDocumentListItem>
              <PolicyDocumentListItem>
                <PolicyDocumentLink
                  id='ProductInformation'
                  href={`${config.BASE_API_URL}/api/motorQuotePolicies/${riskData.QuoteId}/documents/31`}
                  target='_blank'
                >
                  <PolicyDocumentLogo name='ProductInformation' />
                  <PolicyDocumentName>
                    Download insurance product information here
                  </PolicyDocumentName>
                </PolicyDocumentLink>
              </PolicyDocumentListItem>
            </PolicyDocumentList>
          </Card>
        </Grid>
        <Grid desktop='100'>
          <ParagraphComponent>
            By proceeding, you confirm that you have read, can meet, and have
            understood the terms and conditions set by{' '}
            {price.UnderwriterDisplayName} as laid out in the full declaration.{' '}
            {isImpounded
              ? 'On this basis, this policy will meet the demands and needs of someone who needs to insure their vehicle in order to release it from a Police Impound.'
              : 'On this basis, this policy will meet the demands and needs of someone who needs to insure a vehicle for a short period.'}
          </ParagraphComponent>
        </Grid>
        <Button
          marginBottom='32px'
          type='submit'
          disabled={!wordingLoaded}
          id='TeslaSubmitDeclarationNextButton'
          fullWidth
          onClick={handleContinueClick}
          data-email={riskData.EmailAddress}
          className={className}
          loading={legalDeclarationButtonDisabled}
        >
          {legalDeclarationButtonDisabled
            ? 'Please wait'
            : 'Agree and Continue'}
        </Button>
      </Wrapper>
      <DeclarationModal
        text={legalDeclaration.FullText}
        id='declarationModal'
        title='Full Declaration'
        hideFullDeclaration={() => setLegalDecOpen(false)}
        isVisible={showLegalDec}
      />
      <DeclarationModal
        text={legalDeclaration.DisallowedOccupations}
        id='disallowedOccupationsModal'
        title='Unacceptable Occupations'
        hideFullDeclaration={() => setOccupationDecOpen(false)}
        isVisible={showOccupationDec}
      />
    </GridContainerWrapper>
  )
}

export default withOptimizely(LegalDeclarationPage)
